const SDK_ID = 'facebook-jssdk';
const SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js';

/** Meta launch selector for WhatsApp Business app (coexistence) onboarding. */
export const EMBEDDED_SIGNUP_FEATURE_COEXISTENCE = 'whatsapp_business_app_onboarding';

let sdkLoadPromise = null;
let sdkInitialized = false;

function trimEnv(value) {
  if (value == null) {
    return '';
  }
  return String(value).trim();
}

/**
 * Load Meta JS SDK once and FB.init with env app id.
 * @returns {Promise<object>} window.FB
 */
export function loadFacebookSdk() {
  const appId = trimEnv(process.env.VUE_APP_META_APP_ID);
  if (!appId) {
    return Promise.reject(new Error('VUE_APP_META_APP_ID is not configured'));
  }

  if (typeof window !== 'undefined' && window.FB && sdkInitialized) {
    return Promise.resolve(window.FB);
  }

  if (sdkLoadPromise) {
    return sdkLoadPromise;
  }

  sdkLoadPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Facebook SDK requires a browser'));
      return;
    }

    window.fbAsyncInit = () => {
      window.FB.init({
        appId,
        autoLogAppEvents: true,
        xfbml: true,
        version: 'v25.0',
      });
      sdkInitialized = true;
      resolve(window.FB);
    };

    if (window.FB) {
      if (!sdkInitialized) {
        window.fbAsyncInit();
      } else {
        resolve(window.FB);
      }
      return;
    }

    if (document.getElementById(SDK_ID)) {
      const waitForFb = setInterval(() => {
        if (window.FB && sdkInitialized) {
          clearInterval(waitForFb);
          resolve(window.FB);
        }
      }, 50);
      setTimeout(() => {
        clearInterval(waitForFb);
        if (window.FB) {
          resolve(window.FB);
        } else {
          reject(new Error('Facebook SDK failed to load'));
        }
      }, 15000);
      return;
    }

    const script = document.createElement('script');
    script.id = SDK_ID;
    script.src = SDK_SRC;
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error('Facebook SDK script failed to load'));
    document.body.appendChild(script);
  });

  return sdkLoadPromise;
}

export function getEmbeddedSignupConfigId() {
  return trimEnv(process.env.VUE_APP_META_ES_CONFIG_ID);
}

export function isEmbeddedSignupConfigured() {
  return Boolean(
    trimEnv(process.env.VUE_APP_META_APP_ID)
    && trimEnv(process.env.VUE_APP_META_ES_CONFIG_ID),
  );
}

/** Dev-only log for Embedded Signup postMessage payloads. */
export function logEmbeddedSignupMessage(payload) {
  if (process.env.NODE_ENV === 'development' && payload) {
    // eslint-disable-next-line no-console
    console.log('[WA_EMBEDDED_SIGNUP]', payload.event, payload);
  }
}

export const EMBEDDED_SIGNUP_FINISH_EVENTS = new Set([
  'FINISH',
  'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING',
]);

/**
 * FB.login options for Embedded Signup.
 * @param {'coexistence'|'dedicated'} connectionMode
 *   coexistence = existing WhatsApp Business app number;
 *   dedicated = new Cloud API number flow.
 */
export function buildEmbeddedSignupLoginOptions(connectionMode) {
  const configId = getEmbeddedSignupConfigId();
  if (!configId) {
    throw new Error('VUE_APP_META_ES_CONFIG_ID is not configured');
  }
  const isCoexistence = connectionMode === 'coexistence';
  const extras = { setup: {} };
  if (isCoexistence) {
    extras.featureType = EMBEDDED_SIGNUP_FEATURE_COEXISTENCE;
    extras.sessionInfoVersion = '3';
  } else {
    extras.featureType = '';
  }
  return {
    config_id: configId,
    response_type: 'code',
    override_default_response_type: true,
    extras,
  };
}
